import data from "@/public/data/landing.json";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/common/Dialog";
import { Phone, X } from "@phosphor-icons/react/dist/ssr";
import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import Typography from "@/shared/components/common/Typography";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/shared/components/common/input-otp";
import { Fragment, useState } from "react";
import useCountdown from "@/shared/hooks/useCountdown";
import Box from "@/shared/components/common/Box";
import { Pulse } from "@/shared/components/common/Animations";

interface ConfirmProps {
  resend: () => void;
  onChangeMobile: () => void;
  confirm: (confirmCode: string) => void;
  mobile: string;
  isConfirming: boolean;
}

export default function Confirm({
  mobile,

  resend,
  onChangeMobile,
  confirm,
  isConfirming,
}: ConfirmProps) {
  const [confirmCode, setConfirmCode] = useState<number | null>(null);
  const {
    header: {
      loginSignupModal: {
        confirm: { title, description, resendCode, btn, stepBackbtn },
      },
    },
  } = data;

  const { counter, reset } = useCountdown(120);

  return (
    <Flex className="flex-col gap-y-4">
      <DialogHeader className="flex flex-col items-start gap-y-2">
        <DialogTitle className="mx-auto mt-4">
          <Typography variant="h3" className="font-semibold leading-relaxed">
            {title}
          </Typography>
        </DialogTitle>
        <DialogDescription className="mx-auto">
          <Flex className="gap-x-2 items-center flex-col">
            <Flex className="gap-1">
              <Typography className="font-medium">
                {description.prefix}
              </Typography>
              <Typography className="font-semibold text-foreground">
                {mobile}
              </Typography>
            </Flex>
            <Typography className="font-medium">
              {description.postfix}
            </Typography>
          </Flex>
        </DialogDescription>
      </DialogHeader>

      {/* Input */}
      <Flex className="flex-col items-center justify-center gap-y-4">
        <InputOTP
          autoFocus
          maxLength={4}
          onChange={(newValue) => {
            setConfirmCode(Number(newValue));
            if (newValue.length === 4) confirm(newValue);
          }}
          render={({ slots }) => (
            <InputOTPGroup className="gap-2" dir="ltr">
              {slots.map((slot, index) => (
                <Fragment key={index}>
                  <InputOTPSlot
                    className="rounded-xs! border border-foreground"
                    {...slot}
                  />
                  {index !== slots.length - 1 && <InputOTPSeparator />}
                </Fragment>
              ))}
            </InputOTPGroup>
          )}
        />

        {counter ? (
          <Typography
            variant="h6"
            className="font-semibold text-muted-foreground"
          >
            {counter}
          </Typography>
        ) : (
          <Box
            onClick={() => {
              resend();
              reset();
            }}
            className="cursor-pointer group"
          >
            <Typography className="group-hover:shadow-1">
              {resendCode}
            </Typography>
          </Box>
        )}
      </Flex>

      {/* Submit button */}
      <DialogFooter>
        <Flex className="w-full flex-col items-center gap-y-2">
          <Button
            className="w-full rounded-full bg-transparent! text-foreground shadow-none border border-foreground hover:bg-foreground! group transition duration-75 ease-in-out"
            onClick={onChangeMobile}
          >
            <Flex className="items-center gap-x-2">
              <Typography className="text-foreground group-hover:text-background! transition duration-75 ease-in-out">
                {stepBackbtn}
              </Typography>
              <Phone className="w-5 h-5 text-foreground group-hover:text-background! transition duration-75 ease-in-out" />
            </Flex>
          </Button>

          <Button
            disabled={
              !confirmCode ||
              confirmCode?.toLocaleString().length < 4 ||
              isConfirming
            }
            className="w-full rounded-full"
            onClick={() => {
              confirm(String(confirmCode));
            }}
          >
            {isConfirming ? <Pulse /> : btn}
          </Button>
        </Flex>
      </DialogFooter>
    </Flex>
  );
}
